import {motion} from "framer-motion"
import {FC, useEffect, useState} from "react"

import {
  WIDGET_BUBBLE_SIZE,
  WIDGET_MARGIN,
  WIDGET_MOBILE_BREAKPOINT,
} from "./styles"

interface Props {
  onClick: () => void
  unreadCount: number
}

const BUBBLE_WIDTH = 54
const BUBBLE_HEIGHT = 188

const BUBBLE_CLASS = "ri2-guru-bubble"
const BUBBLE_CSS = `
  @keyframes ri2GuruBubbleBorderSpin {
    from { transform: rotate(0deg);   }
    to   { transform: rotate(360deg); }
  }
  @keyframes ri2GuruBubbleGlow {
    0%, 100% { box-shadow: 0 4px 24px rgba(0, 225, 255, 0.45); }
    50%      { box-shadow: 0 4px 36px rgba(0, 225, 255, 0.70); }
  }
  .${BUBBLE_CLASS} {
    position: relative;
    overflow: hidden;
    border-radius: 10px;
    animation: ri2GuruBubbleGlow 2.4s ease-in-out infinite;
    transition: box-shadow 0.25s ease;
  }
  .${BUBBLE_CLASS}.${BUBBLE_CLASS}--round {
    border-radius: 50%;
    animation: ri2GuruBubbleGlow 2.4s ease-in-out infinite,
               ri2GuruBubbleFloat 3.6s ease-in-out infinite;
  }
  .${BUBBLE_CLASS}::before {
    content: "";
    position: absolute;
    inset: -60%;
    z-index: 0;
    pointer-events: none;
    background: conic-gradient(
      from 90deg,
      #3A8BC4   0deg,
      #00E1FF  66deg,
      #91CCF0 118deg,
      #3A8BC4 178deg,
      #91CCF0 232deg,
      #81DDF7 268deg,
      #70EEFF 307deg,
      #3A8BC4 360deg
    );
    animation: ri2GuruBubbleBorderSpin 6s linear infinite;
  }
  .${BUBBLE_CLASS}::after {
    content: "";
    position: absolute;
    inset: 2px;
    z-index: 1;
    border-radius: 8px;
    pointer-events: none;
    background: linear-gradient(180deg, #015DA0 0%, #009EC8 100%);
  }
  .${BUBBLE_CLASS}.${BUBBLE_CLASS}--round::before {
    inset: -30%;
  }
  .${BUBBLE_CLASS}.${BUBBLE_CLASS}--round::after {
    inset: 3px;
    border-radius: 50%;
    background:
      radial-gradient(circle at 30% 28%, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 45%),
      linear-gradient(180deg, #015DA0 0%, #009EC8 100%);
  }
  @keyframes ri2GuruBubbleFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-3px); }
  }
  @keyframes ri2GuruBubbleSparkleTwinkle {
    0%, 100% { transform: scale(1) rotate(0deg);   opacity: 1; }
    50%      { transform: scale(1.12) rotate(12deg); opacity: 0.92; }
  }
  @keyframes ri2GuruUnreadPulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.45);
    }
    50% {
      transform: scale(1.12);
      box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
    }
  }
  .${BUBBLE_CLASS}--round .ri2-guru-sparkle {
    animation: ri2GuruBubbleSparkleTwinkle 2.4s ease-in-out infinite;
    transform-origin: center;
  }
  .${BUBBLE_CLASS} > * {
    position: relative;
    z-index: 2;
  }
  .${BUBBLE_CLASS}:hover {
    animation-play-state: paused;
    box-shadow: 0 6px 32px rgba(0, 225, 255, 0.55);
  }
`

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < WIDGET_MOBILE_BREAKPOINT
      : false,
  )

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth < WIDGET_MOBILE_BREAKPOINT)
    }

    window.addEventListener("resize", handleResize)
    return (): void => window.removeEventListener("resize", handleResize)
  }, [])

  return isMobile
}

const SparkleIcon: FC<{size: number}> = ({size}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M11.999 1.83203C12.7991 4.068 14.222 6.21072 15.9922 7.98828C17.7608 9.76426 19.8989 11.1971 22.1484 11.999C19.8988 12.8009 17.7608 14.2347 15.9922 16.0107C14.2219 17.7885 12.7992 19.932 11.999 22.168C11.1988 19.9323 9.77768 17.7892 8.00781 16.0117C6.23917 14.2355 4.10123 12.8011 1.85156 11.999C4.10107 11.1971 6.2393 9.76426 8.00781 7.98828C9.77771 6.21088 11.1988 4.06773 11.999 1.83203Z"
      fill="#00A9D7"
      stroke="url(#ri2GuruBubbleStarGradient)"
      strokeMiterlimit="7.1853"
    />
    <defs>
      <linearGradient
        id="ri2GuruBubbleStarGradient"
        x1="-21.75"
        y1="0"
        x2="46.5"
        y2="1.15988e-08"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#015DA0" />
        <stop offset="0.182692" stopColor="#00E1FF" />
        <stop offset="0.326923" stopColor="#91CCF0" />
        <stop offset="0.483516" stopColor="#015DA0" />
        <stop offset="0.494505" stopColor="#00E1FF" />
        <stop offset="0.644231" stopColor="#91CCF0" />
        <stop offset="0.745192" stopColor="#81DDF7" />
        <stop offset="0.8525" stopColor="#70EEFF" />
        <stop offset="1" stopColor="#00A9D7" />
      </linearGradient>
    </defs>
  </svg>
)

const UnreadBadge: FC<{count: number}> = ({count}) => (
  <motion.span
    initial={{scale: 0}}
    animate={{scale: 1}}
    style={{
      position: "absolute",
      top: -6,
      right: -6,
      minWidth: 24,
      height: 24,
      padding: "0 7px",
      borderRadius: "50%",
      background:
        "linear-gradient(180deg, #FF6B6B 0%, #EF4444 55%, #DC2626 100%)",
      border: "1px solid rgba(1, 93, 160, 0.55)",
      boxShadow:
        "0 4px 10px rgba(0, 0, 0, 0.18), 0 0 0 2px rgba(0, 169, 215, 0.18)",
      zIndex: 3,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#FFFFFF",
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 1,
      animation: "ri2GuruUnreadPulse 1.4s ease-in-out infinite",
    }}
  >
    {count > 9 ? "9+" : count}
  </motion.span>
)

export const ChatBubble: FC<Props> = ({onClick, unreadCount}) => {
  const isMobile = useIsMobile()
  const ariaLabel =
    unreadCount > 0
      ? `Open Repair Guru, ${unreadCount} unread message${unreadCount === 1 ? "" : "s"}`
      : "Open Repair Guru"

  const sharedWrapperStyle = {
    position: "fixed" as const,
    right: WIDGET_MARGIN,
    zIndex: 9998,
    overflow: "visible" as const,
  }

  const sharedButtonStyle = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }

  if (isMobile) {
    return (
      <motion.div
        initial={{scale: 0, opacity: 0, y: 20}}
        animate={{scale: 1, opacity: 1, y: 0}}
        exit={{scale: 0, opacity: 0, y: 20}}
        transition={{type: "spring", stiffness: 260, damping: 20}}
        style={{
          ...sharedWrapperStyle,
          bottom: WIDGET_MARGIN + 10,
          width: WIDGET_BUBBLE_SIZE,
          height: WIDGET_BUBBLE_SIZE,
          pointerEvents: "none",
        }}
      >
        <style>{BUBBLE_CSS}</style>
        <motion.button
          onClick={onClick}
          whileHover={{scale: 1.04}}
          whileTap={{scale: 0.97}}
          aria-label={ariaLabel}
          className={`${BUBBLE_CLASS} ${BUBBLE_CLASS}--round`}
          style={{
            width: "100%",
            height: "100%",
            ...sharedButtonStyle,
            pointerEvents: "auto",
          }}
        >
          <span className="ri2-guru-sparkle" style={{display: "inline-flex"}}>
            <SparkleIcon size={28} />
          </span>
        </motion.button>
        {unreadCount > 0 && <UnreadBadge count={unreadCount} />}
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{scale: 0, opacity: 0, x: 20}}
      animate={{scale: 1, opacity: 1, x: 0}}
      exit={{scale: 0, opacity: 0, x: 20}}
      transition={{type: "spring", stiffness: 260, damping: 20}}
      style={{
        ...sharedWrapperStyle,
        top: "50%",
        marginTop: -(BUBBLE_HEIGHT / 2),
        width: BUBBLE_WIDTH,
        height: BUBBLE_HEIGHT,
        pointerEvents: "none",
      }}
    >
      <style>{BUBBLE_CSS}</style>
      <motion.button
        onClick={onClick}
        whileHover={{scale: 1.04}}
        whileTap={{scale: 0.97}}
        aria-label={ariaLabel}
        className={BUBBLE_CLASS}
        style={{
          width: "100%",
          height: "100%",
          ...sharedButtonStyle,
          pointerEvents: "auto",
        }}
      >
        {/* Expand icon, top */}
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            color: "#FFFFFF",
            opacity: 0.95,
          }}
        >
          <path
            d="M12.5 4H16V7.5M7.5 16H4V12.5M16 4L11.5 8.5M4 16L8.5 11.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {/* Vertical "REPAIR GURU" label (reads bottom-up) */}
        <span
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, calc(-50% - 4px)) rotate(-90deg)",
            transformOrigin: "center",
            color: "#FFFFFF",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.18em",
            whiteSpace: "nowrap",
          }}
        >
          {"REPAIR GURU"}
        </span>

        {/* Sparkle icon, bottom */}
        <div
          style={{
            position: "absolute",
            bottom: 12,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <SparkleIcon size={22} />
        </div>
      </motion.button>
      {unreadCount > 0 && <UnreadBadge count={unreadCount} />}
    </motion.div>
  )
}
